import { UserAccountType } from './user-data';

interface SearchParamsBase {
    limit: number;
    limits: Array<number>; // [10, 25, 50, 75, 100];
    page: number;
    pages: number;
    pageArray: Array<number>;
    totalRecords: number;
    sort: string;
    sorts: [];
    nextUrl?: string;
    prevUrl?: string;
}

export type SearchParams = SearchParamsBase & {
    name: string;
    searchText: string;
    countryName: string;
};

export type SearchResult = {
    data: any;
    totalRecords: number;
};

export const defaultCancellationParams = {
    name: '',
    limit: 10,
    limits: [10, 25, 50, 75, 100],
    page: 0,
    pages: 0,
    pageArray: [],
    totalRecords: 0,
    sort: 'cancellation_reason.name',
    sorts: [],
} as SearchParams;

export const defaultContactsParams = {
    searchText: '',
    limit: 10,
    limits: [10, 25, 50, 75, 100],
    page: 0,
    pages: 0,
    pageArray: [],
    totalRecords: 0,
    sort: 'contacts.firstname',
    sorts: [],
} as SearchParams;

export const defaultTradeServiceParams = {
    name: '',
    limit: 10,
    limits: [10, 25, 50, 75, 100],
    page: 0,
    pages: 0,
    pageArray: [],
    totalRecords: 0,
    sort: 'company_service.name',
    sorts: [],
} as SearchParams;

export const defaultTradeServiceTypeParams = {
    name: '',
    limit: 10,
    limits: [10, 25, 50, 75, 100],
    page: 0,
    pages: 0,
    pageArray: [],
    totalRecords: 0,
    sort: 'company_service_type.name',
    sorts: [],
} as SearchParams;

export const defaultCountryParams = {
    countryName: '',
    limit: 10,
    limits: [10, 25, 50, 75, 100],
    page: 0,
    pages: 0,
    pageArray: [],
    totalRecords: 0,
    sort: 'countries.countryName',
    sorts: [],
} as SearchParams;

export function adjustSearchParams(data: SearchResult, params: SearchParams): SearchParams {
    params.pages = Math.ceil(data.totalRecords / params.limit);
    params.totalRecords = data.totalRecords;
    params.pageArray = [];
    for (let i = 0; i < params.pages; i++) {
        params.pageArray.push(i);
    }

    return params;
}

export type OrderSearchParams = SearchParamsBase & {
    dashboard: boolean;
    customer?: string;
    customerId?: string;
    orderId?: string;
    invoiceId?: string;
    supplierId?: string;
    supplier?: string;
    status?: string;
    isSample: number;
    dueDate?: string;
    shippingStatus?: string;
    freeText?: string;
    thisWeek: boolean;
    thisMonth: boolean;
    all?: boolean;
    // sort: 'orders.createdAt DESC' | 'orders.updateRequested' | 'contacts.firstname';
};

export const defaultOrderSearchParams = {
    dashboard: false,
    orderId: '',
    invoiceId: '',
    supplierId: '',
    supplier: '',
    status: '',
    isSample: 0,
    dueDate: undefined,
    shippingStatus: '',
    freeText: '',
    thisWeek: false,
    thisMonth: false,
    limit: 10,
    limits: [10, 25, 50, 75, 100],
    page: 0,
    pages: 0,
    pageArray: [],
    totalRecords: 0,
    sort: 'orders.createdAt DESC',
    sorts: [],
} as OrderSearchParams;

export type CallLogsSearchParams = Omit<SearchParams, 'prevUrl' | 'nextUrl' | 'sort'> & {
    phoneNr: Array<string> | null;
    customerId: string | null;
    accountType: UserAccountType | '';
    date: string | undefined;
    direction: 'in' | 'out' | undefined;
    ascOrDesc: 'ASC' | 'DESC';
    sort: 'twilio_calls.createdAt' | 'twilio_calls.duration';
};

export const defaultCallLogsSearchParams = {
    limit: 25,
    limits: [10, 25, 50, 75, 100],
    page: 0,
    pages: 0,
    pageArray: [],
    totalRecords: 0,
    sorts: [],
    phoneNr: null,
    direction: undefined,
    date: undefined,
    accountType: '',
    sort: 'twilio_calls.createdAt',
    ascOrDesc: 'DESC',
} as CallLogsSearchParams;

export type CallRecordingsSearchParams = Omit<SearchParams, 'prevUrl' | 'nextUrl' | 'sort'> & {
    phoneNr: Array<string> | null;
    direction: 'in' | 'out' | undefined;
    accountType: UserAccountType | '';
    date: string | undefined;
    ascOrDesc: 'ASC' | 'DESC';
    sort: 'twilio_calls.createdAt' | 'twilio_calls.duration';
    source: 'RecordVerb' | undefined;
};

export const defaultRecordingsLogsSearchParams = {
    limit: 25,
    limits: [10, 25, 50, 75, 100],
    page: 0,
    pages: 0,
    pageArray: [],
    totalRecords: 0,
    sorts: [],
    phoneNr: null,
    direction: undefined,
    accountType: '',
    date: undefined,
    ascOrDesc: 'DESC',
    sort: 'twilio_calls.createdAt',
    source: undefined
} as CallRecordingsSearchParams;

export type CustomerSearchParams = SearchParamsBase & {
    company: string;
    name: string;
    email: string;
    town: string;
    add1: string;
    postcode: string;
    mobile: string;
    landline: string;
    status: string;
    freeText: string;
    companyId: string;
    accountType: 'customer';
    username: any;
    prospect: 0 | 1;
};

export const defaultCustomerSearch = {
    company: '',
    name: '',
    email: '',
    add1: '',
    town: '',
    postcode: '',
    mobile: '',
    landline: '',
    status: '',
    freeText: '',
    companyId: '',
    accountType: 'customer',
    limit: 25,
    limits: [25, 50, 75, 100],
    page: 0,
    pages: 0,
    pageArray: [],
    totalRecords: 0,
    sort: 'contacts.firstname',
    sorts: [],
    prospect: 0,
    username: '',
} as CustomerSearchParams;

export type ProspectSearchParams = Omit<CustomerSearchParams, 'accountType'> & {
    favorite: 0 | 1;
    prospect: 0 | 1 | undefined;
    createdAt: string | null;
    accountType: UserAccountType | '';
    ascOrDesc: 'ASC' | 'DESC';
};

export const defaultProspectSearchParams = {
    ...defaultCustomerSearch,
    accountType: '',
    favorite: 0,
    prospect: undefined,
    sort: 'contacts.createdAt',
    ascOrDesc: 'ASC',
} as ProspectSearchParams;

export type OrderBoardSearchParams = CustomerSearchParams & {
    dashboard: boolean;
    isSample: number;
    search: string;
    customer: string;
    orderId: string;
    invoiceId: string;
    supplierId: string;
    supplier: string | null;
    allocatedTo: string[];
    createdAt: string | null;
    dueDate: string | null;
    shippingStatus: string;
    thisWeek: boolean;
    thisMonth: boolean;
    labels: Array<any>;
    paid?: boolean;
    noCache?: boolean;
    groupIds?: Array<number>;
};

export const defaultOrderBoardSearchParams = {
    groupIds: [],
    dashboard: false,
    isSample: 0,
    customer: '',
    orderId: '',
    invoiceId: '',
    supplierId: '',
    supplier: null,
    status: '',
    allocatedTo: [],
    createdAt: null,
    dueDate: null,
    shippingStatus: '',
    freeText: '',
    thisWeek: false,
    thisMonth: false,
    limit: 9999,
    limits: [25, 50, 75, 100],
    page: 0,
    pages: 0,
    pageArray: [],
    totalRecords: 0,
    sort: 'orders.dueDate',
    sorts: [],
    labels: [],
    paid: null,
    search: '',
    noCache: false,
} as OrderBoardSearchParams;

export type CompanySearchParams = SearchParamsBase & {
    company: string;
    name: string;
    add1: string;
    town: string;
    postcode: string;
    telephone: string;
    companyNumber: string;
    vatNumber: string;
    accountType: 'supplier' | 'trade' | 'courier';
    status?: any;
    searchText?: string;
    publicId?: string;
    freeText: string;
    range?: string;
};

export const defaultCompanySearchParams = {
    company: '',
    name: '',
    town: '',
    postcode: '',
    telephone: '',
    companyNumber: '',
    vatNumber: '',
    accountType: 'supplier',
    status: '',
    freeText: '',
    limit: 999,
    limits: [25, 50, 75, 100],
    page: 0,
    pages: 0,
    pageArray: [],
    totalRecords: 0,
    sort: 'companies.name',
    sorts: [],
} as CompanySearchParams;

export type PaymentsSearchParams = SearchParamsBase & {
    freeText: string;
    customer: string;
    userId: string;
    orderId: number | '';
    customerId: string;
    status: string;
    all: boolean;
    thisWeek: boolean;
    thisMonth: boolean;
};

export const defaultPaymentsSearchParams = {
    freeText: '',
    customer: '',
    userId: '',
    orderId: '',
    customerId: '',
    status: '0',
    all: true,
    thisWeek: false,
    thisMonth: false,
    limit: 10,
    limits: [10, 25, 50, 75, 100],
    page: 0,
    pages: 0,
    pageArray: [],
    totalRecords: 0,
    sort: 'payments.createdAt DESC',
    sorts: [],
} as PaymentsSearchParams;
